/* eslint-disable relay/unused-fields */

import React from 'react'
import { csx } from '@vtex/admin-ui'
import { useHistory } from 'react-router'
import {
  ProductCarouselItem,
  ProductCarouselItemTitle,
} from '@/checkout-instore-legacy/components/ProductCarousel'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'

import { useOrderForm } from '../../../../../../core/requests/checkout'
import type { ProductsShelfItem as PropsProductsShelfItem } from './types'
import type {
  ProductsShelfItem_ProductConnection$data,
  ProductsShelfItem_ProductConnection$key,
} from './__generated__/ProductsShelfItem_ProductConnection.graphql'
import {
  ProductClickableArea,
  ProductImage,
  ProductImageUnavailable,
  ProductNameUnavailable,
  ProductPrice,
  ProductPromotionHighlight,
  ProductPromotions,
} from '../../../../../../core/components'
import { useFeatureFlag } from '../../../../../../core/hooks'
import { getNewProductPageUrl } from '../../../../../../core/utils/productPage'
import { NodeId } from '../../../../../../core/utils/relay/NodeId'

const productFragment = graphql`
  fragment ProductsShelfItem_ProductConnection on ProductConnection {
    edges {
      node {
        _id
        id
        name
        defaultImage {
          url(size: 420)
        }
        skus {
          edges {
            node {
              _id
              id
              name
              defaultImage {
                url(size: 420)
              }
              offers(addresses: $addresses) {
                edges {
                  node {
                    sku {
                      _id
                      id
                    }
                    seller {
                      _id
                    }
                    deliveryChannel
                    listPrice {
                      amount
                    }
                    netPrice {
                      amount
                    }
                    priority
                  }
                }
                ...ProductSkuAvailabilityDelivery_ProductSkuOfferConnection
                ...ProductSkuAvailabilityPickup_ProductSkuOfferConnection
                ...ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection
              }
              promotions(addresses: $addresses) {
                edges {
                  node {
                    id
                    name
                    highlight
                  }
                }
              }
              images {
                edges {
                  node {
                    id
                    url(size: 420)
                  }
                }
              }
            }
          }
        }
        offers(addresses: $addresses) {
          edges {
            node {
              sku {
                id
                _id
              }
              seller {
                _id
              }
              deliveryChannel
              listPrice {
                amount
              }
              netPrice {
                amount
              }
              priority
            }
          }
          ...ProductSkuAvailabilityDelivery_ProductSkuOfferConnection
          ...ProductSkuAvailabilityPickup_ProductSkuOfferConnection
          ...ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection
        }
        promotions(addresses: $addresses) {
          edges {
            node {
              id
              name
              highlight
            }
          }
        }
        ...ProductSkuRef_Product
      }
    }
  }
`

const getProductOrSkuData = (
  product: ProductsShelfItem_ProductConnection$data['edges'][number],
  productShelfItem: PropsProductsShelfItem
) => {
  const productOrSku =
    (productShelfItem.skuId
      ? product.node.skus.edges.find(
          (skuEdge) => skuEdge.node._id === productShelfItem.skuId
        )
      : product) ?? product

  // TODO: create util inline fragment
  const offers = productOrSku.node.offers?.edges.map((offerEdge) => ({
    deliveryChannel: offerEdge.node.deliveryChannel,
    sellerId: offerEdge.node.seller._id,
    priority: offerEdge.node.priority,
    netPrice: {
      amount: offerEdge.node.netPrice.amount,
    },
    listPrice: {
      amount: offerEdge.node.listPrice.amount,
    },
    productSkuId: offerEdge.node.sku.id,
  }))

  const promotions = productOrSku.node.promotions?.edges.map(
    (promotionEdge) => ({
      id: promotionEdge.node.id,
      name: promotionEdge.node.name,
      highlight: !!promotionEdge.node.highlight,
    })
  )

  return {
    imageUrl: productOrSku.node.defaultImage.url,
    name: productOrSku.node.name,
    offers,
    promotions,
  }
}

export type ProductsShelfItemProps = {
  item: PropsProductsShelfItem
  productConnectionReference: ProductsShelfItem_ProductConnection$key
}

export const ProductsShelfItem = ({
  item,
  productConnectionReference,
}: ProductsShelfItemProps) => {
  const { getCurrencyCode } = useOrderForm()
  const history = useHistory()
  const { isNewProductPageEnabled } = useFeatureFlag()

  const productConnection = useFragment<ProductsShelfItem_ProductConnection$key>(
    productFragment,
    productConnectionReference
  )

  const product = productConnection.edges.find(
    (productEdge) => productEdge.node._id === item.productId
  )

  if (!product) {
    return null
  }

  const productOrSku = getProductOrSkuData(product, item)
  const isProductAvailable = !!productOrSku.offers?.length

  const getPdpSkuId = () => {
    if (item.skuId) {
      return NodeId.encode('ProductSku', item.skuId)
    }

    return (
      productOrSku.offers?.[0]?.productSkuId ??
      product.node.skus.edges[0]?.node.id
    )
  }

  const handleItemClick = () => {
    if (isNewProductPageEnabled()) {
      const skuId = getPdpSkuId()

      const productPageUrl = getNewProductPageUrl(
        {
          skuId,
          productId: product.node.id,
        },
        false
      )

      return history.push(productPageUrl)
    }

    const skuId = product.node.offers?.edges[0]?.node.sku._id

    history.push(
      `/product/${item.productId}?skuId=${skuId}&previousPage=advertisements`
    )
  }

  return (
    <ProductCarouselItem data-testid="products-shelf-item">
      <ProductClickableArea onClick={handleItemClick}>
        {/* TODO: mover this availability logic to Product component */}
        {isProductAvailable ? (
          <>
            <ProductImage alt={productOrSku.name} src={productOrSku.imageUrl} />
            <ProductCarouselItemTitle
              title={product.node.name}
              className={csx({ marginBottom: '$space-1' })}
            >
              {product.node.name}
            </ProductCarouselItemTitle>
          </>
        ) : (
          <>
            <ProductImageUnavailable
              src={productOrSku.imageUrl}
              alt={productOrSku.name}
            />
            <ProductNameUnavailable>{productOrSku.name}</ProductNameUnavailable>
          </>
        )}
        <ProductPrice
          isBusy={false}
          currencyCode={getCurrencyCode()}
          purchaseConditionsPrices={[]}
          offers={productOrSku.offers}
        />
      </ProductClickableArea>
      <ProductPromotions
        promotions={productOrSku.promotions}
        className={csx({ marginTop: '$space-4' })}
      />
      <ProductPromotionHighlight promotions={productOrSku.promotions} />
    </ProductCarouselItem>
  )
}
